import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import AutoReplyMessagesSection from './AutoReplyMessagesSection';
import { FlowModeControl } from './FlowModeControl';
import HubJourneyTools from './HubJourneyTools';
import { TemplateEditor } from './HubTemplatesTab';
import { UI, textButtonSx } from './settingsUi';
import { approvalSummary, TemplateStatusChip, WhatsappPhonePreview } from './hubTemplates';
import type { TemplateStatusesApi } from './hubTemplates';
import {
  AUTO_REPLY_FLOWS, AUTO_REPLY_LANGS, AUTO_REPLY_MESSAGES_DOC, builtInContent,
  buttonKey, mergeContent, messageButtons, textKey,
} from '../../utils/whatsappAutoReplyFlows';
import type { AutoReplyContent, AutoReplyLang } from '../../utils/whatsappAutoReplyFlows';
import { WHATSAPP_SETTINGS_COLLECTION, getFlowMode, isFlowEnabled } from '../../utils/whatsappWorkingHours';
import type { FlowMode, WhatsappAutoReplySettings } from '../../utils/whatsappWorkingHours';
import { JOURNEY_AUTOMATIONS } from '../../utils/whatsappJourney';
import { TEMPLATE_DRAFTS, templatePreviewText } from '../../utils/whatsappTemplateDrafts';

type Selection = { kind: 'journey' | 'flow'; key: string };

const Connector = ({ dashed = false }: { dashed?: boolean }) => (
  <Box aria-hidden="true" sx={{ height: 29, width: 2, mx: 'auto', borderLeft: `2px ${dashed ? 'dashed' : 'solid'} ${UI.gold}`, position: 'relative',
    '&::after': { content: '""', position: 'absolute', bottom: 0, left: -5, width: 7, height: 7, borderRight: `2px solid ${UI.gold}`, borderBottom: `2px solid ${UI.gold}`, transform: 'rotate(45deg)' } }} />
);

const BranchConnector = ({ count }: { count: number }) => count < 2 ? <Connector dashed /> : (
  <>
    <Box sx={{ display: { xs: 'block', sm: 'none' } }}><Connector dashed /></Box>
    <Box aria-hidden="true" sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
      position: 'relative', maxWidth: 880, width: '100%', mx: 'auto', height: 42,
      '&::before': { content: '""', position: 'absolute', top: 17, left: `${50 / count}%`, right: `${50 / count}%`, borderTop: `2px dashed ${UI.gold}` },
      '&::after': { content: '""', position: 'absolute', top: 0, left: '50%', height: 17, borderLeft: `2px dashed ${UI.gold}` } }}>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={{ position: 'relative',
          '&::before': { content: '""', position: 'absolute', top: 17, left: '50%', height: 19, borderLeft: `2px dashed ${UI.gold}` },
          '&::after': { content: '""', position: 'absolute', top: 31, left: 'calc(50% - 4px)', width: 7, height: 7, borderRight: `2px solid ${UI.gold}`, borderBottom: `2px solid ${UI.gold}`, transform: 'rotate(45deg)' } }} />
      ))}
    </Box>
  </>
);

const Node = ({ label, children, accent = UI.gold }: { label: string; children: React.ReactNode; accent?: string }) => (
  <Box sx={{ width: '100%', maxWidth: 880, mx: 'auto', bgcolor: UI.surface, border: `1px solid ${UI.line}`, borderLeft: `4px solid ${accent}`,
    borderRadius: 2, px: 2, py: 1.5, boxShadow: '0 2px 7px rgba(30, 24, 16, 0.06)' }}>
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.7, color: UI.muted, mb: 0.6 }}>{label}</Typography>
    {children}
  </Box>
);

const buttonOutcome = (raw: string | undefined, index: number, label: string, count: number, fallback: string) => {
  const entries = String(raw || '').split(' · ').map((part) => part.split(' → '));
  const normalized = label.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const matched = entries.find(([title]) => title?.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() === normalized);
  return matched?.[1] || (entries.length === count ? entries[index]?.[1] : '') || fallback;
};

const linkedFlow = (label: string) => {
  const value = label.toLowerCase().replace(/^[^a-z0-9]+/, '').trim();
  if (['send brochure', 'brochure', 'share prices', 'send details'].includes(value)) return 'brochure';
  if (['book site visit', 'visit today', 'visit again'].includes(value)) return 'siteVisit';
  if (['call me', 'call me later', 'request a call'].includes(value)) return 'callback';
  if (['explore projects', 'select project', 'view projects'].includes(value)) return 'projectExplore';
  if (['stop updates', 'stop'].includes(value)) return 'optOut';
  return null;
};

const conversationOutcome = (id: string, flowKey: string) => {
  if (id === 'brochure') return 'Brochure flow → PDF and next options';
  if (id === 'visit') return 'Site-visit flow → choose a slot';
  if (id === 'callback') return 'Call-back flow → choose a time';
  if (id === 'projects') return 'Choose a project → possession timeline and next options';
  if (id === 'slots') return 'Choose a slot → visit confirmation';
  if (['morning', 'afternoon', 'evening'].includes(id)) return 'Call-back confirmed → team follow-up';
  if (id === 'stop') return 'Stop automatic updates';
  return flowKey === 'siteVisit' ? 'Visit request goes to the team' : 'Continue this conversation';
};

const CustomerFlowCanvas = ({ selection, settings, templates, onModeChange, onNavigate }: {
  selection: Selection;
  settings: WhatsappAutoReplySettings;
  templates: TemplateStatusesApi;
  onModeChange: (key: string, title: string, mode: FlowMode) => void;
  onNavigate: (next: Selection) => void;
}) => {
  const [lang, setLang] = useState<AutoReplyLang>('en');
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [liveContent, setLiveContent] = useState<AutoReplyContent>(() => builtInContent());
  const defaultContent = useMemo(() => builtInContent(), []);

  useEffect(() => {
    setEditingTemplate(null);
    setSelectedMessage(null);
    setNotice(null);
  }, [selection.kind, selection.key]);

  useEffect(() => onSnapshot(
    doc(db, WHATSAPP_SETTINGS_COLLECTION, AUTO_REPLY_MESSAGES_DOC),
    (snap) => setLiveContent(mergeContent(snap.data()?.published || snap.data()?.draft, defaultContent)),
    () => setLiveContent(defaultContent)
  ), [defaultContent]);

  const automation = selection.kind === 'journey' ? JOURNEY_AUTOMATIONS.find((item) => item.key === selection.key) : null;
  const flow = selection.kind === 'flow' ? AUTO_REPLY_FLOWS.find((item) => item.key === selection.key) : null;
  const mode = getFlowMode(settings, selection.key);
  const on = mode !== 'off';

  if (!automation && !flow) return null;

  return (
    <Box id="customer-flow-editor" sx={{ scrollMarginTop: 12, bgcolor: UI.canvas, border: `1px solid ${UI.line}`, borderRadius: 2.5, p: { xs: 1.5, md: 2.5 },
      backgroundImage: 'radial-gradient(#dedad4 1px, transparent 1px)', backgroundSize: '18px 18px' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: UI.ink }}>Edit this flow on the diagram</Typography>
          <Typography sx={{ fontSize: 12, color: UI.muted }}>Select a message card to edit its wording and buttons. Branches show what each button does.</Typography>
        </Box>
        <Stack direction="row" gap={0.5}>
          {AUTO_REPLY_LANGS.map(({ key, label }) => (
            <Button key={key} size="small" variant={lang === key ? 'contained' : 'outlined'} onClick={() => setLang(key)}
              sx={{ ...textButtonSx, bgcolor: lang === key ? UI.gold : UI.surface, color: lang === key ? UI.surface : UI.ink,
                '&:hover': { bgcolor: lang === key ? UI.goldHover : UI.canvas } }}>{label}</Button>
          ))}
        </Stack>
      </Stack>
      {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ maxWidth: 880, mx: 'auto', mb: 1 }}>{notice}</Alert>}

      <Node label="STARTS WHEN" accent={on ? '#3f9a48' : UI.faint}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: UI.ink, flex: 1 }}>{automation?.title || flow?.title}</Typography>
          <FlowModeControl mode={mode} label={automation?.title || flow?.title || selection.key}
            onChange={(next) => onModeChange(selection.key, automation?.title || flow?.title || selection.key, next)} />
        </Stack>
        <Typography sx={{ fontSize: 12.5, color: UI.muted, lineHeight: 1.55 }}>{automation?.steps[0]?.title || flow?.startsWhen}</Typography>
        {flow && (liveContent.triggerPhrases[flow.key] || []).length > 0 && (
          <Typography sx={{ fontSize: 12.5, color: UI.ink, lineHeight: 1.55, mt: 0.5 }}>
            Or customer writes: {liveContent.triggerPhrases[flow.key].map((phrase) => `“${phrase}”`).join(' · ')}
          </Typography>
        )}
      </Node>

      {automation && (
        <>
          <Connector />
          <Node label="CONDITIONS · CHECKED BEFORE SENDING" accent="#5b8297">
            <Typography sx={{ fontSize: 12.5, color: UI.ink }}>This automation’s mode, opt-out, language approval and skip rules must allow the send.</Typography>
            <Box component="details" sx={{ mt: 0.75, '& summary': { cursor: 'pointer', color: UI.gold, fontSize: 12, fontWeight: 600 } }}>
              <summary>Show all skip rules</summary>
              <Box component="ul" sx={{ mt: 0.5, pl: 2.5, fontSize: 12, color: UI.muted, lineHeight: 1.6 }}>
                {automation.skipWhen.map((reason) => <li key={reason}>{reason}</li>)}
              </Box>
            </Box>
            <Stack direction="row" gap={1} sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: 11.5, color: '#2f6b35' }}>✓ Eligible → continue</Typography>
              <Typography sx={{ fontSize: 11.5, color: UI.muted }}>✕ Ineligible → skipped</Typography>
            </Stack>
          </Node>
          <Connector />
          <Node label="TIMING / CRM ACTION" accent="#6d83a5">
            {automation.steps.map((step) => (
              <Typography key={step.title} sx={{ fontSize: 12.5, color: UI.ink, lineHeight: 1.6 }}>• {step.title}</Typography>
            ))}
            {automation.tool && <Box sx={{ mt: 1 }}><HubJourneyTools tool={automation.tool} /></Box>}
          </Node>
          {selection.key === 'enquiryWelcome' && <>
            <Connector />
            <Node label="PROJECT CHECK" accent="#5b8297">
              <Typography sx={{ fontSize: 12.5, color: UI.ink }}>CRM finds a published project for this lead → send the project welcome. Otherwise → send the project chooser. Only one template is sent.</Typography>
            </Node>
            <BranchConnector count={2} />
          </>}
          <Box sx={selection.key === 'enquiryWelcome' ? { maxWidth: 1100, width: '100%', mx: 'auto', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, alignItems: 'start' } : {}}>
          {automation.templates.map((name) => {
            const base = TEMPLATE_DRAFTS.find((draft) => draft.name === name);
            const draft = templates.effective(name) || base;
            if (name === 't2_site_visit_brochure' && !draft) {
              const { approved, total } = approvalSummary([name], templates);
              return <React.Fragment key={name}>
                <Connector />
                <Node label={`SEND WHATSAPP TEMPLATE · ${approved}/${total} APPROVED`} accent={UI.gold}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: UI.ink, flex: 1, fontFamily: UI.mono }}>{name}</Typography>
                    <TemplateStatusChip lang="en" status={templates.statusFor(name, 'en')} />
                  </Stack>
                  <Typography sx={{ fontSize: 12.5, color: UI.muted, mt: 1 }}>
                    Existing English Meta template. Sends the project brochure PDF and a project details button after a completed visit.
                  </Typography>
                </Node>
              </React.Fragment>;
            }
            if (!draft || !base) return null;
            const { approved, total } = approvalSummary([name], templates);
            return (
              <React.Fragment key={name}>
                {selection.key !== 'enquiryWelcome' && <Connector />}
                <Node label={`SEND WHATSAPP TEMPLATE${automation.templateConditions?.[name] ? ` · ${automation.templateConditions[name]}` : ''} · ${approved}/${total} LANGUAGES APPROVED`} accent={UI.gold}>
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: UI.ink, flex: 1, fontFamily: UI.mono }}>{name}</Typography>
                    {AUTO_REPLY_LANGS.map(({ key }) => <TemplateStatusChip key={key} lang={key} status={templates.statusFor(name, key)} />)}
                    <Button size="small" variant="outlined" onClick={() => setEditingTemplate(editingTemplate === name ? null : name)}
                      sx={textButtonSx}>{editingTemplate === name ? 'Close editor' : 'Edit message & buttons'}</Button>
                  </Stack>
                  {editingTemplate === name ? (
                    <Box sx={{ borderTop: `1px solid ${UI.line}`, mt: 1.25, pt: 1 }}>
                      <TemplateEditor key={name} base={base} api={templates} initialLanguage={lang} onDone={(message) => setNotice(`${name}: ${message}`)} />
                    </Box>
                  ) : (
                    <Box sx={{ maxWidth: 430, mt: 1.25 }}>
                      <WhatsappPhonePreview body={templatePreviewText(draft, lang)} footer={draft.footer?.[lang]}
                        buttons={(draft.buttons || []).map((button) => button[lang])}
                        buttonUrls={draft.urlButton ? [draft.urlButton.sample] : undefined} />
                      {draft.urlButton && <Typography sx={{ fontSize: 11.5, color: UI.muted, mt: 0.75 }}>
                        Sample link: {draft.urlButton.sample}. {draft.urlButton.note}
                      </Typography>}
                    </Box>
                  )}
                </Node>
                {(draft.buttons || []).length > 0 && (
                  <>
                    <BranchConnector count={draft.buttons!.length} />
                    <Box sx={{ maxWidth: 880, width: '100%', mx: 'auto', display: 'grid', gridTemplateColumns: selection.key === 'enquiryWelcome' ? '1fr' : { xs: '1fr', sm: `repeat(${Math.min(draft.buttons!.length, 3)}, minmax(0, 1fr))` }, gap: 1 }}>
                      {draft.buttons!.map((button, index) => (
                        <Node key={index} label={`BUTTON ${index + 1}`} accent="#5b8297">
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: UI.ink }}>{button[lang]}</Typography>
                          <Typography sx={{ fontSize: 11.5, color: UI.muted, mt: 0.35 }}>{buttonOutcome(automation.templateButtonReplies?.[name] || automation.buttonReplies, index, button.en, draft.buttons!.length, 'Opens the linked reply or alerts the team')}</Typography>
                          {linkedFlow(button.en) && <Button size="small" onClick={() => onNavigate({ kind: 'flow', key: linkedFlow(button.en)! })} sx={{ ...textButtonSx, px: 0, mt: 0.5 }}>Open linked flow · {isFlowEnabled(settings, linkedFlow(button.en)!) ? 'On' : 'Off'} →</Button>}
                        </Node>
                      ))}
                    </Box>
                  </>
                )}
              </React.Fragment>
            );
          })}
          {(automation.manualTemplates || []).map((name) => {
            const base = TEMPLATE_DRAFTS.find((draft) => draft.name === name);
            const draft = templates.effective(name) || base;
            if (!draft || !base) return null;
            const { approved, total } = approvalSummary([name], templates);
            return (
              <React.Fragment key={`manual-${name}`}>
                <Connector />
                <Node label={`MANUAL WHATSAPP TEMPLATE · ${approved}/${total} LANGUAGES APPROVED`} accent="#8a6a3a">
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: UI.ink, flex: 1, fontFamily: UI.mono }}>{name}</Typography>
                    {AUTO_REPLY_LANGS.map(({ key }) => <TemplateStatusChip key={key} lang={key} status={templates.statusFor(name, key)} />)}
                    <Button size="small" variant="outlined" onClick={() => setEditingTemplate(editingTemplate === name ? null : name)} sx={textButtonSx}>
                      {editingTemplate === name ? 'Close editor' : 'Edit message'}
                    </Button>
                  </Stack>
                  <Typography sx={{ fontSize: 12.5, color: UI.muted, mt: 1 }}>
                    Staff sends this payment-details PDF to the primary unit holder from Office → Unit detail → Payment transactions. It is not sent automatically.
                  </Typography>
                  {editingTemplate === name ? (
                    <Box sx={{ borderTop: `1px solid ${UI.line}`, mt: 1.25, pt: 1 }}>
                      <TemplateEditor key={name} base={base} api={templates} initialLanguage={lang} onDone={(message) => setNotice(`${name}: ${message}`)} />
                    </Box>
                  ) : (
                    <Box sx={{ maxWidth: 430, mt: 1.25 }}>
                      <WhatsappPhonePreview body={templatePreviewText(draft, lang)} footer={draft.footer?.[lang]} />
                    </Box>
                  )}
                </Node>
              </React.Fragment>
            );
          })}
          </Box>
          {selection.key === 'enquiryWelcome' && <>
            <Connector />
            <Node label="WAIT FOR CUSTOMER" accent="#5b8297">
              <Typography sx={{ fontSize: 12.5, color: UI.ink, lineHeight: 1.6 }}>
                The welcome template can start the WhatsApp conversation without an earlier customer message. If the customer taps a button or replies, continue with the linked conversation flow. If they do not respond, the sales team can call them; only a separately approved follow-up template may be sent later.
              </Typography>
            </Node>
          </>}
        </>
      )}

      {flow && (
        <>
          {flow.messages.map((message) => {
            const buttons = messageButtons(message);
            const open = selectedMessage === message.key;
            return (
              <React.Fragment key={message.key}>
                <Connector />
                <Node label={message.attachment === 'brochure' ? 'SEND BROCHURE + MESSAGE' : message.role === 'footer' ? 'FOOTER' : 'SEND CONVERSATION MESSAGE'} accent={UI.gold}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: UI.ink, flex: 1 }}>{message.label}</Typography>
                    <Button size="small" variant="outlined" onClick={() => setSelectedMessage(open ? null : message.key)} sx={textButtonSx}>
                      {open ? 'Close editor' : 'Edit message & buttons'}
                    </Button>
                  </Stack>
                  <Typography sx={{ fontSize: 12.5, color: UI.muted, whiteSpace: 'pre-line', lineHeight: 1.5, mt: 0.8 }}>
                    {liveContent.texts[textKey(flow.key, message.key)]?.[lang] || message.defaults[lang]}
                  </Typography>
                  {open && <Box sx={{ mt: 1.5 }}><AutoReplyMessagesSection flowKey={flow.key} hideFlowNav editorOnly selectedMessageKey={message.key} /></Box>}
                </Node>
                {buttons.length > 0 && (
                  <>
                    <BranchConnector count={buttons.length} />
                    <Box sx={{ maxWidth: 880, width: '100%', mx: 'auto', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(buttons.length, 3)}, minmax(0, 1fr))` }, gap: 1 }}>
                      {buttons.map((button) => (
                        <Node key={button.id} label="CUSTOMER TAPS" accent="#5b8297">
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: UI.ink }}>{liveContent.buttons[buttonKey(flow.key, message.key, button.id)]?.[lang] || button.defaults[lang]}</Typography>
                          <Typography sx={{ fontSize: 11.5, color: UI.muted, mt: 0.35 }}>{conversationOutcome(button.id, flow.key)}</Typography>
                          {linkedFlow(button.defaults.en) && <Button size="small" onClick={() => onNavigate({ kind: 'flow', key: linkedFlow(button.defaults.en)! })} sx={{ ...textButtonSx, px: 0, mt: 0.5 }}>Open linked flow · {isFlowEnabled(settings, linkedFlow(button.defaults.en)!) ? 'On' : 'Off'} →</Button>}
                        </Node>
                      ))}
                    </Box>
                  </>
                )}
              </React.Fragment>
            );
          })}
          {flow.action && <><Connector /><Node label="CRM ACTION" accent="#7e57c2"><Typography sx={{ fontSize: 12.5, color: UI.ink }}>{flow.action}</Typography></Node></>}
          {flow.end && <><Connector /><Node label="END"><Typography sx={{ fontSize: 12.5, color: UI.ink }}>{flow.end}</Typography></Node></>}
        </>
      )}
    </Box>
  );
};

export default CustomerFlowCanvas;
